export interface SubmissionKeyState {
  getOrCreate(): string;
  invalidate(): void;
}

export function createSubmissionKeyState(
  createKey: () => string = () => crypto.randomUUID(),
): SubmissionKeyState {
  let key: string | null = null;
  return {
    getOrCreate(): string {
      key ??= createKey();
      return key;
    },
    invalidate(): void {
      key = null;
    },
  };
}

export async function submitRunRequest(
  request: (path: string, init?: RequestInit) => Promise<Response>,
  body: FormData,
  keys: SubmissionKeyState,
): Promise<void> {
  const response = await request("/api/runs", {
    method: "POST",
    headers: { "idempotency-key": keys.getOrCreate() },
    body,
  });
  const payload = (await response.json()) as { error?: unknown };
  if (!response.ok) {
    throw new Error(typeof payload.error === "string" ? payload.error : `HTTP ${response.status}`);
  }
  keys.invalidate();
}

export interface LocalPreviewRegistryOptions {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
}

export interface LocalPreviewRegistry {
  create(blob: Blob): string;
  release(url: string): void;
  clear(): void;
}

export function createLocalPreviewRegistry(
  options: LocalPreviewRegistryOptions = {
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
  },
): LocalPreviewRegistry {
  const urls = new Set<string>();
  return {
    create(blob: Blob): string {
      const url = options.createObjectURL(blob);
      urls.add(url);
      return url;
    },
    release(url: string): void {
      if (!urls.delete(url)) return;
      options.revokeObjectURL(url);
    },
    clear(): void {
      for (const url of urls) options.revokeObjectURL(url);
      urls.clear();
    },
  };
}
